/**
 * Sashiko patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const sashikoPatternsPack: ToilePack = {
    slug: 'sashiko-patterns',
    category: 'craft',
    name: 'Sashiko patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'asanoha', label: 'Asanoha', subject: 'an asanoha hemp-leaf sashiko pattern of six-pointed stars drawn in even dashed running stitches over a faint triangular grid, the stitches stopping short of each crossing'},
        {key: 'seigaiha', label: 'Seigaiha', subject: 'a seigaiha wave sashiko pattern of overlapping fan-shaped arcs in dashed running stitches, laid over a faint square grid with the arc centres dotted'},
        {key: 'hitomezashi', label: 'Hitomezashi', subject: 'a hitomezashi sashiko pattern where single stitches on a square grid meet to form a cross-and-step design, each stitch one grid square long and the grid itself faint'},
    ],
    styles: [
        {key: 'dashed', label: 'Dashed stitches', style: 'even dashed running-stitch lines, each dash the same length with a short gap, over a faint light grid', palette: 'black dashes on a pure white ground', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'tracing', label: 'Tracing guide', style: 'a single continuous guide line for tracing onto fabric, the repeat marked by small tick marks on the border', palette: 'thin black line on a pure white ground', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'indigo', label: 'White on indigo', style: 'even white dashed stitch lines as on a classic indigo cloth, the grid a faint lighter blue', palette: 'white dashes on a deep indigo ground', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['coaster', 'cushion', 'red thread'],
    directionHint: 'try: coaster',
    seo: {
        title: 'Sashiko patterns: asanoha, seigaiha, hitomezashi',
        metaDescription: 'Nine sashiko stitching guides: asanoha stars, seigaiha waves and hitomezashi crosses, as dashed lines and tracing guides.',
        h1: 'Sashiko patterns: asanoha, seigaiha and hitomezashi',
        tagline: 'Traditional Japanese running-stitch patterns, drawn as even dashed lines ready to trace.',
        intro: 'Sashiko is Japanese running-stitch embroidery that began as a way to mend and strengthen cloth. People stitch it on coasters, cushions and visible mends. These nine guides draw three traditional patterns: asanoha, seigaiha and hitomezashi. Each comes as even dashed lines, a single tracing line, or white dashes on indigo.',
        useCases: [
            'a coaster',
            'a cushion front',
            'a visible mend on denim',
            'a tea towel edge',
        ],
        faq: [
            {q: 'Can I trace these onto fabric?', a: 'Yes. The tracing guide is one continuous line with tick marks for the repeat. The dashed style shows the stitches directly.'},
            {q: 'What is hitomezashi?', a: 'A sashiko pattern in which single stitches on a grid meet to form the design.'},
            {q: 'Is the white on indigo a different pattern?', a: 'No. It is the same dashed style in white on indigo, as on classic indigo cloth.'},
        ],
    },
}
